import { Alert, Box, Chip, Divider, IconButton, Link, Stack, Tooltip, Typography } from "@mui/material";
import React from "react"
import { PinChip } from "../MessageProfile";
import { formatPhoneNumber } from "../../app/utils";
import PhoneIcon from '@mui/icons-material/Phone';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import LoadingButton from '@mui/material/Button'
import { StartChat } from "./MMMarkers";
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { DirectChatUsers, useCreateOrJoinGroupChatMutation, useSetChatUserMutation } from '../../features/ChatSlice'
import { useAuthProfile } from "../../app/hooks"
import { useNavigate } from "react-router-dom";
import EmailIcon from '@mui/icons-material/Email';
import GroupIcon from '@mui/icons-material/Group';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { useUpdateProfileGroupMutation } from "../../features/profilesSlice";
import { showSupergroups } from "../..";

const ExternalLink: React.FC<{ href: string; label: string }> = ({ href, label }) => (
  <Link
    target="_blank"
    rel="noopener noreferrer"
    href={href}
    variant="body2"
    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
  >
    {label}
    <OpenInNewIcon sx={{ fontSize: '0.75rem' }} />
  </Link>
)

// Shared colored header: name (linked if website) + close button
const CardHeader: React.FC<{ name: string; webSite?: string; onClose?: () => void }> = ({ name, webSite, onClose }) => (
  <Stack
    direction="row"
    alignItems="flex-start"
    justifyContent="space-between"
    sx={{ background: 'linear-gradient(135deg, rgba(107,33,168,0.92) 0%, rgba(130,50,190,0.88) 100%)', px: 1.5, py: 1, flexShrink: 0, borderRadius: '12px 12px 0 0', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)' }}
  >
    {webSite ? (
      <Link
        target="_blank"
        rel="noopener noreferrer"
        href={webSite}
        underline="hover"
        sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.3, flexGrow: 1, pr: 0.5 }}
      >
        {name}
      </Link>
    ) : (
      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.3, flexGrow: 1, pr: 0.5 }}>
        {name}
      </Typography>
    )}
    {onClose && (
      <IconButton
        size="small"
        onClick={onClose}
        sx={{ p: 0.25, color: 'rgba(255,255,255,0.8)', flexShrink: 0, mt: '-2px', mr: '-4px',
          '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.15)' } }}
      >
        <CloseIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    )}
  </Stack>
)

// topActions: CTA button(s) shown first in the body so they're visible without scrolling on mobile
export const InfoWindowDetails: React.FC<any> = ({ data, topActions, onClose, children, containerSx }) => {
  const hasContactInfo = !!(data.address || data.phone || data.email)
  return (
    <Stack direction="column" sx={{ width: '280px', maxHeight: '360px', overflow: 'hidden', borderRadius: '12px', background: 'linear-gradient(145deg, rgba(244, 240, 255, 0.97) 0%, rgba(236, 232, 250, 0.95) 50%, rgba(244, 240, 255, 0.97) 100%)', border: '1px solid rgba(164, 142, 233, 0.22)', boxShadow: '0 8px 32px rgba(101, 110, 255, 0.12), 0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.6)', ...containerSx }}>
      <CardHeader name={data.name} webSite={data.web_site} onClose={onClose} />
      {/* Fixed: badge and CTA — never scroll away */}
      <Stack direction="column" gap={0.75} sx={{ px: 1.5, pt: 1, pb: 0.5, flexShrink: 0 }}>
        {data.type && <PinChip category="Institution" type={data.type} size="small" iconSizePx={16} sx={{ pt: 0.5, pb: 0.5, height: '24px', fontSize: '0.7rem' }} />}
        {topActions}
      </Stack>

      {/* Scrollable: contact info, description, links */}
      <Stack direction="column" gap={0.75} sx={{ px: 1.5, pb: 1, overflow: 'auto', flexGrow: 1 }}>
        {(hasContactInfo || data.description) && <Divider sx={{ my: 0.25 }} />}
        {data.address && (
          <Stack direction="row" gap={0.5} alignItems="flex-start">
            <RoomOutlinedIcon fontSize="small" color="primary" sx={{ flexShrink: 0, mt: '1px' }} />
            <Typography variant="caption" color="text.secondary">{data.address}</Typography>
          </Stack>
        )}
        {data.phone && (
          <Stack direction="row" gap={0.5} alignItems="center">
            <PhoneIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">{formatPhoneNumber(data.phone)}</Typography>
          </Stack>
        )}
        {data.email && (
          <Stack direction="row" gap={0.5} alignItems="center" sx={{ overflow: 'hidden' }}>
            <EmailIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
            <Link href={`mailto:${data.email}`} underline="always" color="primary" variant="caption" noWrap>
              {data.email}
            </Link>
          </Stack>
        )}
        {data.description && (
          <Box sx={{ borderLeft: '3px solid #E0DEFF', pl: 1.5, py: 0.5, backgroundColor: '#FAFAFA', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>{data.description}</Typography>
          </Box>
        )}
        <div>{children}</div>
      </Stack>
    </Stack>
  );
}

export const CoeInfoWindowDetails: React.FC<any> = ({ data, onClose, containerSx }) => {
  const navigate = useNavigate()
  const [SetChat, { isLoading: isLoadingSetChat, isError: isErrorSetChat }] = useCreateOrJoinGroupChatMutation()
  const { authStatus, profile } = useAuthProfile()

  if (isErrorSetChat)
    return <Alert severity="error">Oops... Error occurred while initiating a chat. Please try again later.</Alert>

  const primaryLinks = [
    data.mm_program_overview?.href && { href: data.mm_program_overview.href, label: data.mm_program_overview.text ?? 'MM Program Overview' },
    data.meet_the_mm_team?.href && { href: data.meet_the_mm_team.href, label: data.meet_the_mm_team.text ?? 'MM Specialists' },
    data.academic_affiliation?.href && { href: data.academic_affiliation.href, label: `Academic Affiliation: ${data.academic_affiliation.text}` },
    data.comment?.href && { href: data.comment.href, label: data.comment.text ?? 'Additional Info' },
    data.clinical_trials?.href && { href: data.clinical_trials.href, label: 'Clinical Trials (Phase I/II)' },
    data.myeloma_magizine?.href && { href: data.myeloma_magizine, label: 'Myeloma Magazine' },
    data.research?.href && { href: data.research, label: 'Research' },
    data.locations?.href && { href: data.locations, label: 'Locations' },
  ].filter(Boolean) as { href: string; label: string }[]

  const socialLinks = [
    data.facebook?.href && { href: data.facebook, label: 'Facebook' },
    data.twitter?.href && { href: data.twitter, label: 'Twitter' },
  ].filter(Boolean) as { href: string; label: string }[]

  return (
    <InfoWindowDetails
      data={data}
      onClose={onClose}
      containerSx={containerSx}
      topActions={showSupergroups ? (
        <LoadingButton
          loading={isLoadingSetChat}
          variant="contained"
          size="small"
          fullWidth
          disableElevation
          startIcon={<PeopleAltIcon />}
          sx={{ textTransform: 'none', fontSize: '0.75rem', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: 'white', '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } }}
          onClick={() => {
            const ids = [profile.id, 1]
            StartChat(ids, authStatus ?? '', navigate,
              async (_setChatArg: DirectChatUsers) => {
                return await SetChat({ loggedInId: profile.id, channelType: 'resource-query', channelName: data.name ?? '', ids: [profile.id, 1] }).unwrap();
              })
          }}
        >
          Join Group Chat
        </LoadingButton>
      ) : undefined}
    >
      <>
        <Stack direction="column" gap={0.25} sx={{ mt: 0.25 }}>
          {primaryLinks.map((link) => (
            <ExternalLink key={link.href} href={link.href} label={link.label} />
          ))}
        </Stack>
        {socialLinks.length > 0 && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" gap={1}>
              {socialLinks.map((link) => (
                <ExternalLink key={link.href} href={link.href} label={link.label} />
              ))}
            </Stack>
          </>
        )}
        {data.number_of_mm_specialists_simplified && (
          <Chip
            size="small"
            icon={<PeopleAltIcon />}
            label={`${data.number_of_mm_specialists_simplified} MM Specialist${data.number_of_mm_specialists_simplified !== 1 ? 's' : ''}`}
            variant="outlined"
            color="primary"
            sx={{ alignSelf: 'flex-start', mt: 0.5 }}
          />
        )}
      </>
    </InfoWindowDetails>
  );
}

export const HousingInforWindowDetails: React.FC<any> = ({ data, onClose, containerSx }) => {
  return (
    <InfoWindowDetails data={data} onClose={onClose} containerSx={containerSx}>
      <>
        {data.description && <Typography variant="body2">{data.description}</Typography>}
      </>
    </InfoWindowDetails>
  );
}

export const SupportGroupInfoWindowDetails: React.FC<any> = ({ data, onClose, containerSx }) => {
  const navigate = useNavigate()
  const [SetChat, { isLoading: isLoadingSetChat }] = useCreateOrJoinGroupChatMutation()
  const [SetDirectChat, { isLoading: isLoadingSetDirectChat }] = useSetChatUserMutation()
  const { authStatus, profile } = useAuthProfile()
  const [UpdateProfileGroup] = useUpdateProfileGroupMutation()
  const alreadyContacted = !!profile?.metadata?.groups?.[data.id]?.contacted

  const contactAction = data.metadata?.leaderId ? (
    <LoadingButton
      variant="contained"
      size="small"
      fullWidth
      disableElevation
      sx={{ borderRadius: '20px', textTransform: 'none', fontSize: '0.75rem', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: 'white', '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } }}
      loading={isLoadingSetDirectChat}
      onClick={() => {
        const ids = [profile.id, data.metadata.leaderId]
        StartChat(ids, authStatus ?? '', navigate,
          async (setChatArg: DirectChatUsers) => {
            const ret = await SetDirectChat({
              ...setChatArg,
              ...!alreadyContacted ? { sendInChat: true, messageChat: `Hi, I'd like to join ${data.name}` } : {},
            }).unwrap()
            const groupObject = { [ret.id]: { contacted: Date.now(), type: 'S' } }
            if (!alreadyContacted) {
              UpdateProfileGroup({ id: profile.id, groupObject: groupObject })
            }
            return ret
          })
      }}
    >
      Contact
    </LoadingButton>
  ) : undefined

  return (
    <InfoWindowDetails data={data} onClose={onClose} topActions={contactAction} containerSx={containerSx}>
      <>
        {data.metadata?.membership && (
          <Tooltip title="Membership requirements" placement="left">
            <Stack direction="row" gap={0.5} alignItems="center">
              <GroupIcon fontSize="small" color="primary" />
              <Typography variant="body2" color="text.secondary">{data.metadata.membership}</Typography>
            </Stack>
          </Tooltip>
        )}
        {data.metadata?.meetingType && (
          <Tooltip title="Meeting setting" placement="left">
            <Stack direction="row" gap={0.5} alignItems="center">
              <MeetingRoomIcon fontSize="small" color="primary" />
              <Typography variant="body2" color="text.secondary">{data.metadata.meetingType}</Typography>
            </Stack>
          </Tooltip>
        )}
        {data.metadata?.meetingSchedule && (
          <Tooltip title="Meeting schedule" placement="left">
            <Stack direction="row" gap={0.5} alignItems="center">
              <ScheduleIcon fontSize="small" color="primary" />
              <Typography variant="body2" color="text.secondary">{data.metadata.meetingSchedule}</Typography>
            </Stack>
          </Tooltip>
        )}
        {data.metadata?.isRegistrationRequired && (
          <Chip size="small" label="Registration Required" color="warning" sx={{ alignSelf: 'flex-start' }} />
        )}
        {(!!!data.metadata?.noGroupChat && showSupergroups) && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <LoadingButton
              variant="contained"
              size="small"
              fullWidth
              disableElevation
              startIcon={<PeopleAltIcon />}
              loading={isLoadingSetChat}
              sx={{ textTransform: 'none', fontSize: '0.75rem', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: 'white', '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } }}
              onClick={() => {
                const ids = [profile.id, 1]
                StartChat(ids, authStatus ?? '', navigate,
                  async (_setChatArg: DirectChatUsers) => {
                    return await SetChat({ loggedInId: profile.id, channelType: 'resource-query', channelName: data.name ?? '', ids: [profile.id, 1] }).unwrap()
                  })
              }}
            >
              Group Chat
            </LoadingButton>
          </>
        )}
      </>
    </InfoWindowDetails>
  );
}

export const ClinicalTrialInfoWindowDetails: React.FC<any> = ({ data, onClose, containerSx }) => {
  const navigate = useNavigate()
  const queryParams = new URLSearchParams({
    clinicalTrial: data.nctId,
    ...data.metadata?.leaderId ? { contactId: data.metadata?.leaderId } : {}
  });

  const ctaActions = (
    <Stack direction="column" gap={0.5}>
      <LoadingButton
        variant="contained"
        size="small"
        fullWidth
        disableElevation
        sx={{ textTransform: 'none', fontSize: '0.75rem', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: 'white', '&:hover': { background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)' } }}
        onClick={() => navigate(`/Resources?${queryParams.toString()}`)}
      >
        Learn More / Contact
      </LoadingButton>
      {data.metadata?.leaderId && (
        <LoadingButton
          variant="outlined"
          size="small"
          fullWidth
          disableElevation
          startIcon={<SmsOutlinedIcon />}
          sx={{ textTransform: 'none', fontSize: '0.75rem', borderColor: '#8B5CF6', color: '#7C3AED', '&:hover': { borderColor: '#6D28D9', bgcolor: 'rgba(139,92,246,0.06)' } }}
          onClick={() => navigate({
            pathname: "/Conversations",
            search: new URLSearchParams({ directId: data.metadata.leaderId, directNamePublic: data.nctId }).toString(),
          })}
        >
          Chat with Leader
        </LoadingButton>
      )}
    </Stack>
  )

  return (
    <InfoWindowDetails data={data} onClose={onClose} topActions={ctaActions} containerSx={containerSx}>
      <>
        {data.nctId && (
          <Chip
            size="small"
            label={data.nctId}
            clickable
            color="primary"
            variant="outlined"
            onClick={() => navigate(`/Resources?${queryParams.toString()}`)}
            sx={{ alignSelf: 'flex-start' }}
          />
        )}
      </>
    </InfoWindowDetails>
  );
}
